/**
 * Minimal, dependency-free, XSS-safe Markdown renderer for assistant messages.
 *
 * Safety model: the raw string is HTML-escaped FIRST, so no model-produced HTML
 * (or javascript: URLs) can ever reach the DOM. Only our own whitelisted tags
 * (strong, code, pre, ul/ol/li, table, h3/h4, p, br, a) are then inserted around
 * the already-escaped text. The result is safe for v-html. Anchors are built by
 * us alone, only from an absolute http(s) URL, and only when its host is one the
 * server vouched for (see resolveLink and AiLinkHosts). Every other address —
 * and every javascript:/data: string — stays inert text, so neither an injected
 * model nor an injected documentation page can hand the user a clickable
 * phishing target. An empty host list therefore renders no links at all.
 *
 * Supported subset: fenced code blocks, GitHub tables, ordered/unordered lists
 * (one level of nesting via indentation), h1-h6 (rendered as h3/h4), bold,
 * inline code, paragraphs, links ([label](url) and bare URLs).
 */

const CODE_TOKEN_PREFIX = "@@CODEBLOCK_";
const CODE_TOKEN_SUFFIX = "@@";

/**
 * Inline code and links are lifted out of the text while the remaining inline
 * markup is processed, so a URL inside backticks stays code and an href already
 * turned into an anchor is never linkified a second time.
 */
const INLINE_TOKEN_PREFIX = "@@INLINE_";
const INLINE_TOKEN_SUFFIX = "@@";

const MARKDOWN_LINK_PATTERN = /\[([^\]\n]*)\]\(\s*([^\s)]+)\s*\)/g;
const BARE_URL_PATTERN = /https?:\/\/[^\s<>()]+/gi;
/**
 * Sentence punctuation (and escaped quotes/brackets) glued to the end of a bare
 * URL belongs to the prose, not to the address.
 */
const TRAILING_NOISE_PATTERN = /(?:[.,;:!?]|&quot;|&gt;|&lt;|&amp;)+$/;
const MAX_LINK_LABEL_CHARS = 64;

const LINK_VARIANTS = ["inline", "block"] as const;

type LinkVariant = (typeof LINK_VARIANTS)[number];

const LINK_SCOPES = ["internal", "external"] as const;

type LinkScope = (typeof LINK_SCOPES)[number];

/**
 * The hosts the server vouched for. `app` is the application's own host, whose
 * links stay in the SPA; every host in `trusted` (the application, the product
 * documentation and the public site) may become a link at all.
 */
export interface AiLinkHosts {
    app: string | null;
    trusted: readonly string[];
}

interface AiLink {
    href: string;
    scope: LinkScope;
}

/** Heroicons "arrow-top-right-on-square": this link leaves the application. */
const EXTERNAL_LINK_ICON =
    '<svg class="ai-md-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>';

/**
 * Heroicons "arrow-right-circle", the glyph the "go to page" button already
 * uses: this link stays inside the application.
 */
const INTERNAL_LINK_ICON =
    '<svg class="ai-md-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12.75 15l3-3m0 0l-3-3m3 3h-7.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>';

/**
 * Marks an anchor the chat component intercepts to navigate inside the SPA
 * instead of letting the browser reload the application.
 */
export const INTERNAL_LINK_ATTRIBUTE = "data-ai-internal";

const ALLOWED_CHART_TYPES = ["bar", "line", "doughnut"] as const;
const MAX_CHART_LABELS = 24;
const MAX_CHART_SERIES = 6;

export type AiChartType = (typeof ALLOWED_CHART_TYPES)[number];

export interface AiChartSeries {
    name: string;
    data: number[];
}

export interface AiChartSpec {
    type: AiChartType;
    title: string | null;
    labels: string[];
    series: AiChartSeries[];
}

export interface AiStatsSpec {
    compliant: number;
    nonCompliant: number;
    complianceRate: number;
    before: { compliant: number; nonCompliant: number } | null;
}

export type AiSegment =
    | { kind: "markdown"; html: string }
    | { kind: "chart"; spec: AiChartSpec }
    | { kind: "stats"; spec: AiStatsSpec };

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function unescapeHtml(value: string): string {
    return value
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&");
}

/**
 * Absolute http(s) URLs on a trusted host only: anything else (an unknown host,
 * javascript:, data:, a relative path, a protocol-relative "//host") is not a
 * link. Takes an HTML-escaped string and returns one, so the href can go
 * straight into an attribute, plus where the link leads.
 */
function resolveLink(escapedUrl: string, hosts: AiLinkHosts): AiLink | null {
    const url = unescapeHtml(escapedUrl).trim();

    if (!/^https?:\/\/[^\s"'<>]+$/i.test(url)) {
        return null;
    }

    let host = "";
    try {
        host = new URL(url).hostname.toLowerCase();
    } catch {
        return null;
    }

    if (!hosts.trusted.includes(host)) {
        return null;
    }

    return {
        href: escapeHtml(url),
        scope: host === hosts.app ? "internal" : "external",
    };
}

/** Bare URLs are shown without protocol (and shortened) so the chip stays readable. */
function urlLabel(escapedUrl: string): string {
    const url = unescapeHtml(escapedUrl)
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "")
        .replace(/\/$/, "");

    return escapeHtml(
        url.length > MAX_LINK_LABEL_CHARS
            ? `${url.slice(0, MAX_LINK_LABEL_CHARS - 1)}…`
            : url,
    );
}

/**
 * One shared anchor for every link: a compact chip inline in prose, the same
 * pill as the "Go to page" button when the link stands on its own line. An
 * address inside the application keeps that button's orange and its arrow and
 * opens in the same tab; one that leaves the application is blue, carries the
 * external-link glyph and opens a new tab, so the two are never mistaken for
 * each other.
 */
function renderLink(
    link: AiLink,
    label: string | null,
    variant: LinkVariant,
): string {
    const text = (label ?? "").replace(/\*\*/g, "").trim();
    const inner = text === "" ? urlLabel(link.href) : text;
    const isBlock = variant === "block";
    const isInternal = link.scope === "internal";
    const icon = isInternal ? INTERNAL_LINK_ICON : EXTERNAL_LINK_ICON;
    const body = isBlock
        ? `${icon}<span class="ai-md-link-label">${inner}</span>`
        : `<span class="ai-md-link-label">${inner}</span>${icon}`;
    const classes = [
        "ai-md-link",
        isBlock ? "ai-md-link-block" : "",
        isInternal ? "ai-md-link-internal" : "",
    ]
        .filter((name) => name !== "")
        .join(" ");
    const target = isInternal
        ? ` ${INTERNAL_LINK_ATTRIBUTE}="true"`
        : ' target="_blank" rel="noopener noreferrer"';

    return `<a class="${classes}" href="${link.href}"${target} title="${link.href}">${body}</a>`;
}

/** A line holding nothing but a link becomes a stand-alone call-to-action. */
function renderStandaloneLink(line: string, hosts: AiLinkHosts): string | null {
    const markdownLink = /^\[([^\]\n]*)\]\(\s*([^\s)]+)\s*\)$/.exec(line);

    if (markdownLink !== null) {
        const link = resolveLink(markdownLink[2] ?? "", hosts);
        return link === null
            ? null
            : `<div class="ai-md-link-row">${renderLink(link, markdownLink[1] ?? null, "block")}</div>`;
    }

    if (!/^https?:\/\/\S+$/i.test(line)) {
        return null;
    }

    const link = resolveLink(line.replace(TRAILING_NOISE_PATTERN, ""), hosts);

    return link === null
        ? null
        : `<div class="ai-md-link-row">${renderLink(link, null, "block")}</div>`;
}

/** Inline formatting on an ALREADY html-escaped string. */
function renderInline(text: string, hosts: AiLinkHosts): string {
    const held: string[] = [];
    const hold = (html: string): string =>
        `${INLINE_TOKEN_PREFIX}${held.push(html) - 1}${INLINE_TOKEN_SUFFIX}`;

    let html = text.replace(/`([^`]+)`/g, (_match, code: string) =>
        hold(`<code>${code}</code>`),
    );

    html = html.replace(
        MARKDOWN_LINK_PATTERN,
        (match, label: string, url: string) => {
            const link = resolveLink(url, hosts);
            return link === null
                ? match
                : hold(renderLink(link, label, "inline"));
        },
    );

    html = html.replace(BARE_URL_PATTERN, (match) => {
        const noise = TRAILING_NOISE_PATTERN.exec(match)?.[0] ?? "";
        const link = resolveLink(
            match.slice(0, match.length - noise.length),
            hosts,
        );

        return link === null
            ? match
            : `${hold(renderLink(link, null, "inline"))}${noise}`;
    });

    html = html.replace(
        /\*\*([^*]+)\*\*/g,
        (_match, bold: string) => `<strong>${bold}</strong>`,
    );

    held.forEach((value, index) => {
        html = html
            .split(`${INLINE_TOKEN_PREFIX}${index}${INLINE_TOKEN_SUFFIX}`)
            .join(value);
    });

    return html;
}

function isTableSeparator(line: string): boolean {
    return /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes("-");
}

function splitTableCells(line: string): string[] {
    return line
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((cell) => cell.trim());
}

function renderTable(
    headerLine: string,
    rows: string[],
    hosts: AiLinkHosts,
): string {
    const headers = splitTableCells(headerLine)
        .map((cell) => `<th>${renderInline(cell, hosts)}</th>`)
        .join("");

    const body = rows
        .map((row) => {
            const cells = splitTableCells(row)
                .map((cell) => `<td>${renderInline(cell, hosts)}</td>`)
                .join("");
            return `<tr>${cells}</tr>`;
        })
        .join("");

    return `<div class="ai-md-table"><table><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table></div>`;
}

interface ListItem {
    indent: number;
    ordered: boolean;
    content: string;
}

function renderList(items: ListItem[], hosts: AiLinkHosts): string {
    let html = "";
    const stack: boolean[] = [];
    let prevDepth = -1;

    const openList = (ordered: boolean): void => {
        html += ordered ? "<ol>" : "<ul>";
        stack.push(ordered);
    };
    const closeList = (): void => {
        html += (stack.pop() ?? false) ? "</ol>" : "</ul>";
    };

    items.forEach((item) => {
        const depth = item.indent >= 2 ? 1 : 0;

        if (depth > prevDepth) {
            for (let d = prevDepth + 1; d <= depth; d += 1) {
                openList(item.ordered);
            }
        } else if (depth < prevDepth) {
            for (let d = prevDepth; d > depth; d -= 1) {
                html += "</li>";
                closeList();
            }
            html += "</li>";
        } else {
            html += "</li>";
        }

        html += `<li>${renderInline(item.content, hosts)}`;
        prevDepth = depth;
    });

    for (let d = prevDepth; d >= 0; d -= 1) {
        html += "</li>";
        closeList();
    }

    return html;
}

export function renderMarkdown(source: string, hosts: AiLinkHosts): string {
    const escaped = escapeHtml((source ?? "").replace(/\r\n/g, "\n"));

    // Extract fenced code blocks so their content is not treated as markdown.
    const codeBlocks: string[] = [];
    const withoutCode = escaped.replace(
        /```[^\n]*\n([\s\S]*?)```/g,
        (_match, code: string) => {
            const index =
                codeBlocks.push(
                    `<pre><code>${code.replace(/\n$/, "")}</code></pre>`,
                ) - 1;
            return `${CODE_TOKEN_PREFIX}${index}${CODE_TOKEN_SUFFIX}`;
        },
    );

    const lines = withoutCode.split("\n");
    const blocks: string[] = [];
    let paragraph: string[] = [];
    let listItems: ListItem[] = [];

    const flushParagraph = (): void => {
        if (paragraph.length > 0) {
            blocks.push(
                `<p>${paragraph.map((line) => renderInline(line, hosts)).join("<br>")}</p>`,
            );
            paragraph = [];
        }
    };

    const flushList = (): void => {
        if (listItems.length > 0) {
            blocks.push(renderList(listItems, hosts));
            listItems = [];
        }
    };

    const isCodeToken = (value: string): boolean =>
        value.startsWith(CODE_TOKEN_PREFIX) &&
        value.endsWith(CODE_TOKEN_SUFFIX);

    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const trimmed = line.trim();

        if (trimmed === "") {
            flushParagraph();

            // A blank line only ends a list if the next non-empty line is not
            // itself a list item (otherwise numbering would restart per block).
            if (listItems.length > 0) {
                let k = i + 1;
                while (k < lines.length && lines[k].trim() === "") {
                    k += 1;
                }
                const next = k < lines.length ? lines[k] : "";
                if (!/^\s*([-*]|\d+\.)\s+/.test(next)) {
                    flushList();
                }
            }

            continue;
        }

        if (isCodeToken(trimmed)) {
            flushParagraph();
            flushList();
            blocks.push(trimmed);
            continue;
        }

        // Table: header row followed by a separator row.
        if (
            line.includes("|") &&
            i + 1 < lines.length &&
            isTableSeparator(lines[i + 1])
        ) {
            flushParagraph();
            flushList();
            const rows: string[] = [];
            let j = i + 2;
            while (
                j < lines.length &&
                lines[j].includes("|") &&
                lines[j].trim() !== ""
            ) {
                rows.push(lines[j]);
                j += 1;
            }
            blocks.push(renderTable(line, rows, hosts));
            i = j - 1;
            continue;
        }

        const heading = /^(#{1,6})\s+(.*)$/.exec(line);
        if (heading) {
            flushParagraph();
            flushList();
            const level = Math.min(heading[1].length + 2, 4); // h3/h4 only
            blocks.push(
                `<h${level}>${renderInline(heading[2], hosts)}</h${level}>`,
            );
            continue;
        }

        const unordered = /^(\s*)[-*]\s+(.*)$/.exec(line);
        const ordered = /^(\s*)\d+\.\s+(.*)$/.exec(line);
        if (unordered || ordered) {
            flushParagraph();
            const match = (unordered ?? ordered) as RegExpExecArray;
            listItems.push({
                indent: match[1].length,
                ordered: ordered !== null,
                content: match[2],
            });
            continue;
        }

        const standaloneLink = renderStandaloneLink(trimmed, hosts);
        if (standaloneLink !== null) {
            flushParagraph();
            flushList();
            blocks.push(standaloneLink);
            continue;
        }

        flushList();
        paragraph.push(trimmed);
    }

    flushParagraph();
    flushList();

    let html = blocks.join("");
    codeBlocks.forEach((block, index) => {
        html = html
            .split(`${CODE_TOKEN_PREFIX}${index}${CODE_TOKEN_SUFFIX}`)
            .join(block);
    });

    return html;
}

function toStringArray(value: unknown, max: number): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.slice(0, max).map((item) => String(item));
}

/** Validate a model-supplied chart spec. Returns null if it is not usable. */
function validateChartSpec(raw: unknown): AiChartSpec | null {
    if (typeof raw !== "object" || raw === null) {
        return null;
    }

    const candidate = raw as Record<string, unknown>;
    const type = candidate.type;

    if (
        typeof type !== "string" ||
        !ALLOWED_CHART_TYPES.includes(type as AiChartType)
    ) {
        return null;
    }

    const labels = toStringArray(candidate.labels, MAX_CHART_LABELS);
    if (labels.length === 0) {
        return null;
    }

    if (!Array.isArray(candidate.series)) {
        return null;
    }

    const series: AiChartSeries[] = [];
    for (const entry of candidate.series.slice(0, MAX_CHART_SERIES)) {
        if (typeof entry !== "object" || entry === null) {
            continue;
        }
        const seriesEntry = entry as Record<string, unknown>;
        if (!Array.isArray(seriesEntry.data)) {
            continue;
        }
        const data = seriesEntry.data
            .slice(0, MAX_CHART_LABELS)
            .map((value) =>
                typeof value === "number" && isFinite(value) ? value : 0,
            );
        series.push({
            name: typeof seriesEntry.name === "string" ? seriesEntry.name : "",
            data,
        });
    }

    if (series.length === 0) {
        return null;
    }

    return {
        type: type as AiChartType,
        title: typeof candidate.title === "string" ? candidate.title : null,
        labels,
        series,
    };
}

function toCount(value: unknown): number {
    return typeof value === "number" && isFinite(value) && value >= 0
        ? value
        : 0;
}

/** Validate a model-supplied dashboard-stats spec. Returns null if unusable. */
function validateStatsSpec(raw: unknown): AiStatsSpec | null {
    if (typeof raw !== "object" || raw === null) {
        return null;
    }

    const candidate = raw as Record<string, unknown>;
    const compliant = toCount(candidate.compliant);
    const nonCompliant = toCount(candidate.nonCompliant);

    if (compliant + nonCompliant === 0) {
        return null;
    }

    const rate =
        typeof candidate.complianceRate === "number" &&
        isFinite(candidate.complianceRate)
            ? candidate.complianceRate
            : Math.round((compliant / (compliant + nonCompliant)) * 100);

    let before: AiStatsSpec["before"] = null;
    if (typeof candidate.before === "object" && candidate.before !== null) {
        const raw2 = candidate.before as Record<string, unknown>;
        before = {
            compliant: toCount(raw2.compliant),
            nonCompliant: toCount(raw2.nonCompliant),
        };
    }

    return { compliant, nonCompliant, complianceRate: rate, before };
}

/**
 * Split assistant content into ordered segments. Fenced ```dmarceye-chart and
 * ```dmarceye-stats blocks carry validated JSON specs (rendered as a chart or a
 * dashboard bar); anything else — or anything that fails validation — is shown
 * as normal markdown.
 */
export function parseAssistantContent(
    source: string,
    hosts: AiLinkHosts,
): AiSegment[] {
    const text = (source ?? "").replace(/\r\n/g, "\n");
    // Match ANY fenced block; we decide by its CONTENT, not by the language tag,
    // so a stats/chart spec renders as a component even if the model wraps it in
    // a plain ``` or ```json fence instead of ```dmarceye-stats / ```dmarceye-chart.
    const pattern = /```([^\n]*)\n([\s\S]*?)```/g;
    const segments: AiSegment[] = [];

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const pushMarkdown = (chunk: string): void => {
        if (chunk.trim() !== "") {
            segments.push({
                kind: "markdown",
                html: renderMarkdown(chunk, hosts),
            });
        }
    };

    while ((match = pattern.exec(text)) !== null) {
        pushMarkdown(text.slice(lastIndex, match.index));
        lastIndex = pattern.lastIndex;

        let parsed: unknown = null;
        try {
            parsed = JSON.parse(match[2].trim());
        } catch {
            parsed = null;
        }

        const chartSpec = validateChartSpec(parsed);
        if (chartSpec !== null) {
            segments.push({ kind: "chart", spec: chartSpec });
            continue;
        }

        const statsSpec = validateStatsSpec(parsed);
        if (statsSpec !== null) {
            segments.push({ kind: "stats", spec: statsSpec });
            continue;
        }

        // Not a recognised spec — keep the original fence as a normal code block.
        pushMarkdown(match[0]);
    }

    pushMarkdown(text.slice(lastIndex));

    if (segments.length === 0) {
        segments.push({
            kind: "markdown",
            html: renderMarkdown(text, hosts),
        });
    }

    return segments;
}
